import { useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import toast from "react-hot-toast";

// One direction of a combined round-trip booking (both directions under ONE TBO
// BookingId). Agent-handled end to end: the panel never calls the TBO cancel,
// because a full cancel of this BookingId would cancel both directions. The
// agent prices the direction, the customer accepts, the agent cancels only these
// sectors with TBO manually and records it here.

const QUOTABLE_STATUSES = ["", "pending_charges", "charges_calculated", "charges_quoted", "charges_notified"];
const POLL_MS = 15000;

export function isDirectionCancelTicket(ticket) {
  return ticket?.cancel_direction === "OUTBOUND" || ticket?.cancel_direction === "RETURN";
}

function parseDetail(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

export default function DirectionCancelPanel({ ticket, ticket_id, booking, formatINR, onTicketRefresh }) {
  const status = ticket?.cancellation_status || "";
  const detail = parseDetail(ticket?.cancel_direction_detail);
  const label = ticket?.cancel_direction === "RETURN" ? "Return" : "Outbound";
  const kept = ticket?.cancel_direction === "RETURN" ? "outbound" : "return";
  const sectors = Array.isArray(detail.sectors) ? detail.sectors : [];
  const completion = detail.completion || null;

  const [charge, setCharge] = useState(ticket?.cancellation_charge != null ? String(Number(ticket.cancellation_charge)) : "");
  const [refund, setRefund] = useState(ticket?.refund_amount_calculated != null ? String(Number(ticket.refund_amount_calculated)) : "");
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [tboRef, setTboRef] = useState("");
  const [airlineRefund, setAirlineRefund] = useState("");
  const [note, setNote] = useState("");
  const [recordSubmitting, setRecordSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // The customer's Accept arrives from the app; pick it up without a reload.
  const refreshRef = useRef(onTicketRefresh);
  refreshRef.current = onTicketRefresh;
  const awaitingCustomer = status === "charges_notified";
  useEffect(() => {
    if (!awaitingCustomer) return undefined;
    const id = setInterval(() => {
      Promise.resolve(refreshRef.current?.()).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(id);
  }, [awaitingCustomer]);

  const totalFare = Number(booking?.totalfare) || null;
  const chargeNum = charge.trim() === "" ? NaN : Number(charge);
  const refundNum = refund.trim() === "" ? NaN : Number(refund);
  const quoteError =
    !Number.isFinite(chargeNum) || chargeNum < 0
      ? "Enter the airline charge for this direction."
      : !Number.isFinite(refundNum) || refundNum < 0
      ? "Enter the refund the customer receives for this direction."
      : totalFare != null && chargeNum + refundNum > totalFare + 0.01
      ? `Charge plus refund exceeds the total fare paid (${formatINR(totalFare)}).`
      : null;
  const canQuote = QUOTABLE_STATUSES.includes(status);
  const canRecord = status === "customer_accepted";

  const sendQuote = async () => {
    if (quoteError) {
      toast.error(quoteError);
      return;
    }
    setQuoteSubmitting(true);
    try {
      await api.post("/admin/tickets/update-cancellation-charge", {
        ticket_id: Number(ticket_id),
        cancellation_charge: chargeNum,
        refund_amount_calculated: refundNum,
      });
      toast.success("Charges sent to the customer");
      await onTicketRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not send the charges");
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const recordCancellation = async () => {
    setConfirmOpen(false);
    setRecordSubmitting(true);
    try {
      await api.post(`/admin/tickets/${ticket_id}/direction-cancellation`, {
        tbo_change_request_id: tboRef.trim(),
        airline_refund_amount: airlineRefund.trim() === "" ? null : Number(airlineRefund),
        note: note.trim(),
      });
      toast.success("Cancellation recorded. Issue the customer's refund next.");
      await onTicketRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not record the cancellation");
      await onTicketRefresh();
    } finally {
      setRecordSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 space-y-2 text-sm text-amber-900">
        <div className="font-bold">
          One direction only: {label} flight {detail.route ? `(${detail.route})` : ""}
        </div>
        <p>
          This booking holds both directions under one TBO BookingId. The customer wants to cancel only the{" "}
          {label.toLowerCase()} flight; their {kept} flight stays booked.
        </p>
        <p className="font-semibold">
          Do not run a full cancellation for this booking: it would cancel both directions with TBO. Cancel only the
          sectors below with TBO manually.
        </p>
        <div className="text-xs">
          <span className="font-semibold">Sectors to cancel: </span>
          {sectors.length ? sectors.map((s) => `${s.Origin}-${s.Destination}`).join(", ") : "not recorded"}
          {detail.departure ? <span> | Departs {detail.departure}</span> : null}
        </div>
      </div>

      {/* Step 1: quote this direction */}
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 space-y-3">
        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Step 1: Send charges for this direction</div>
        <p className="text-xs text-gray-500">
          Get the airline charge and refund for the {label.toLowerCase()} flight from TBO or the airline. The system
          cannot calculate a one-direction refund, so enter both amounts.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs text-gray-600 font-medium">
            Airline cancellation charge (₹)
            <input
              type="number"
              min="0"
              value={charge}
              onChange={(e) => setCharge(e.target.value)}
              disabled={!canQuote || quoteSubmitting}
              className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
            />
          </label>
          <label className="text-xs text-gray-600 font-medium">
            Refund to customer (₹)
            <input
              type="number"
              min="0"
              value={refund}
              onChange={(e) => setRefund(e.target.value)}
              disabled={!canQuote || quoteSubmitting}
              className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
            />
          </label>
        </div>
        {canQuote ? (
          <button
            type="button"
            onClick={sendQuote}
            disabled={quoteSubmitting || quoteError != null}
            title={quoteError || undefined}
            className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {quoteSubmitting ? "Sending…" : status === "charges_notified" ? "Re-send charges to customer" : "Send charges to customer"}
          </button>
        ) : null}
        {status === "charges_notified" && (
          <div className="rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-900">
            Waiting for the customer to Accept in the app. Their Accept does not cancel anything; it unlocks Step 2.
            This page updates on its own.
          </div>
        )}
        {status === "customer_declined" && (
          <div className="rounded-xl border border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-700">
            The customer declined. Nothing is cancelled.
          </div>
        )}
      </div>

      {/* Step 2: record the manual TBO cancellation */}
      <div className={`rounded-xl border px-4 py-3 space-y-3 ${canRecord ? "border-red-300 bg-white" : "border-gray-200 bg-white opacity-60"}`}>
        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Step 2: Record manual cancellation</div>
        {completion ? (
          <div className="rounded-xl border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-900 space-y-1">
            <div className="font-semibold">Cancelled with TBO (ref {completion.tbo_change_request_id})</div>
            <div>
              Recorded by {completion.recorded_by || "agent"} on {completion.recorded_at}
              {completion.airline_refund_amount != null ? ` | Airline refund ${formatINR(completion.airline_refund_amount)}` : ""}
            </div>
            <div>Note: {completion.note}</div>
            <div className="font-semibold pt-1">
              Refund to customer{ticket?.refund_amount_calculated != null ? ` of ${formatINR(Number(ticket.refund_amount_calculated))}` : ""}{" "}
              must still be issued manually.
            </div>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500">
              {canRecord
                ? `The customer accepted. Cancel only the ${label.toLowerCase()} sectors with TBO (PartialCancellation with these Sectors and every passenger's TicketId), then record it here.`
                : "Unlocks after the customer accepts the charges. Do not cancel with TBO before that."}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-xs text-gray-600 font-medium">
                TBO ChangeRequestId / reference
                <input
                  value={tboRef}
                  onChange={(e) => setTboRef(e.target.value)}
                  disabled={!canRecord || recordSubmitting}
                  className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
                />
              </label>
              <label className="text-xs text-gray-600 font-medium">
                Airline refund from TBO (₹, optional)
                <input
                  type="number"
                  min="0"
                  value={airlineRefund}
                  onChange={(e) => setAirlineRefund(e.target.value)}
                  disabled={!canRecord || recordSubmitting}
                  className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
                />
              </label>
            </div>
            <label className="text-xs text-gray-600 font-medium block">
              Note (what was cancelled and how)
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={!canRecord || recordSubmitting}
                className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black resize-none disabled:opacity-60"
              />
            </label>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!canRecord || recordSubmitting || !tboRef.trim() || !note.trim()}
              className="w-full rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {recordSubmitting ? "Recording…" : "Record manual cancellation"}
            </button>
          </>
        )}
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="text-base font-bold text-gray-900">Record this cancellation?</div>
            <p className="text-sm text-gray-700">
              Confirm you have already cancelled only the {label.toLowerCase()} sectors (
              {sectors.map((s) => `${s.Origin}-${s.Destination}`).join(", ")}) with TBO. The customer will be told their{" "}
              {label.toLowerCase()} flight is cancelled.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={recordCancellation}
                className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700"
              >
                Yes, record it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
